import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';

type Theme = 'dark' | 'light';

interface ThemeState {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
  isDark: boolean;
}

const FALLBACK: ThemeState = {
  theme: 'dark',
  setTheme: () => {},
  toggle: () => {},
  isDark: true,
};

const ThemeContext = createContext<ThemeState>(FALLBACK);

const STORAGE_KEY = 'fb-theme';
const TRANSITION_CLASS = 'fb-theme-transition';
const TRANSITION_MS = 300;

function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'dark';
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'dark' || stored === 'light') return stored;
  // Default to dark — matches the platform's monochrome design language
  return 'dark';
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);
  const themeRef = useRef(theme);

  // Issue 1: sync DOM class with state on mount and whenever theme changes
  useEffect(() => {
    themeRef.current = theme;
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem(STORAGE_KEY)) {
        const next = e.matches ? 'dark' : 'light';
        setThemeState(next);
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const setTheme = useCallback((next: Theme) => {
    const root = document.documentElement;
    root.classList.add(TRANSITION_CLASS);
    localStorage.setItem(STORAGE_KEY, next);
    setThemeState(next);
    setTimeout(() => root.classList.remove(TRANSITION_CLASS), TRANSITION_MS);
  }, []);

  // Issue 5: no side effects inside state updater — read current value from ref
  const toggle = useCallback(() => {
    const next = themeRef.current === 'dark' ? 'light' : 'dark';
    setTheme(next);
  }, [setTheme]);

  const value = useMemo(
    () => ({ theme, setTheme, toggle, isDark: theme === 'dark' }),
    [theme, setTheme, toggle],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
